import React from "react";
import { FiLock } from "react-icons/fi";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { Link } from "react-router-dom";

import styles from "./new.module.css";
import { getRegExp, getSentenceFromCamelCase } from "./Helpers";
import { InputProps } from "../../types/form";

type PasswordInputProps = InputProps & {
  linkOption?: {
    url: string;
    text: string;
  };
};

const PasswordInput = ({
  checked,
  className,
  disabled,
  error,
  fixLength,
  required,
  label,
  minLength,
  maxLength,
  name,
  onChange,
  placeholder,
  reqType,
  style,
  validationHandler,
  value,
  size = "medium",
  labelClassName,
  linkOption,
}: PasswordInputProps) => {
  const [show, setShow] = React.useState(false);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;

    const inputValue = type === "checkbox" ? checked : value;

    onChange && onChange(name, inputValue);
  };

  const onValidationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!validationHandler) return;

    const { value } = event.target;
    let errorMessage = "";

    if (!value && required) {
      errorMessage = `Please enter ${getSentenceFromCamelCase(name)}.`;
    } else if (minLength && value.length < minLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be at least ${minLength} characters long.`;
    } else if (maxLength && value.length > maxLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be ${maxLength} characters long.`;
    } else if (fixLength && value.length !== fixLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be ${fixLength} characters.`;
    } else if (value && reqType) {
      const regExp = getRegExp(reqType) as RegExp;

      if (!regExp.test(value)) {
        errorMessage = `Please enter valid ${getSentenceFromCamelCase(name)}.`;
      }
    }

    validationHandler(name, errorMessage);
  };

  return (
    <div
      className={`${styles.InputContainer} ${styles[size || ""]} ${
        error ? styles.hasError : ""
      }`}
    >
      <label
        htmlFor={name}
        className={`${labelClassName || styles.inputLabel} ${
          styles.passwordLabel
        }`}
      >
        <p>
          {label} {required && <span className={styles.required}>&nbsp;*</span>}
        </p>

        {linkOption && <Link to={linkOption.url}>{linkOption.text}</Link>}
      </label>

      <div className={styles.fieldShell}>
        <span className={styles.fieldIcon} aria-hidden="true">
          <FiLock />
        </span>

        <input
          type={show ? "text" : "password"}
          name={name}
          id={name}
          value={value as string}
          checked={checked}
          placeholder={placeholder}
          className={`${styles.blackBorder} ${styles.modernInput} ${
            styles.passwordField
          } ${className || ""} ${error ? styles.borderRed : ""}`}
          required={required}
          style={style}
          disabled={disabled}
          onChange={onChangeHandler}
          onBlur={onValidationChange}
        />

        <button
          type="button"
          className={styles.passwordIcon}
          onClick={() => setShow((prev) => !prev)}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <FaRegEye size={18} /> : <FaRegEyeSlash size={18} />}
        </button>
      </div>

      {error ? (
        <span className={`${styles.required} ${styles.textSmall}`}>
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default PasswordInput;
